// `extension-alert-candidates-slim.tpl.html:4` — the automation row above the candidate list:
// the tenant's ATS logo, and either "Activate automation" or a switch showing its state.
//
// ⚠️ IT IS THE SAME THREE-WAY STATE THE ALERT TAB'S BADGE SHOWS, and the first of the three is
// again the ABSENCE of a mode:
//
//   !automationMode          → the "Activate automation" button
//   automationMode Enabled   → a switch, on,  labelled "Automation active"
//   automationMode Paused    → a switch, off, labelled "Automation paused"
//
// So `automationMode` is not a boolean with a third case bolted on; there is no mode until
// automation has been turned on for that alert, and `not-enabled` on the wrapper is what
// styles the button variant.
//
// The whole row is behind `ng-if="::shortlistHunterAutomationService.isEnabled"` — a service
// flag, not the per-alert mode — which is why the caller passes it rather than this inferring
// it from `automationMode` being undefined. Those two mean different things: automation off
// for the TENANT hides the row; automation unset for the ALERT shows the button.
import type { ReactElement } from 'react'
import { WooSwitch } from '../common/WooSwitch.tsx'
import { asset } from '../assetBase.ts'

/** `HunterAutomationMode`. */
export const HunterAutomationMode = {
  Enabled: 'Enabled',
  Paused: 'Paused',
} as const
export type HunterAutomationMode = (typeof HunterAutomationMode)[keyof typeof HunterAutomationMode]

/** `ATSTypeAutomationLogo` (shortlist.constants.ng1.ts:166) — one entry, keyed by ATS name. */
export const ATS_AUTOMATION_LOGO: Readonly<Record<string, string>> = {
  Bullhorn: '/static/assets/img/extension/ats-automation/bullhorn.png',
}

export interface HunterAutomationBannerProps {
  /** `window.careero.atsName`, which keys the logo. */
  atsName?: string
  /** The alert's mode. Undefined or null means automation has never been turned on for it. */
  automationMode?: HunterAutomationMode | null
  onActivate?: () => void
  onToggle?: () => void
}

export function HunterAutomationBanner({
  atsName,
  automationMode,
  onActivate,
  onToggle,
}: HunterAutomationBannerProps): ReactElement {
  const active = automationMode === HunterAutomationMode.Enabled
  const hasMode = automationMode !== undefined && automationMode !== null
  const logo = atsName === undefined ? undefined : ATS_AUTOMATION_LOGO[atsName]

  return (
    <div className={`hunter-automation-wrap slim-filters-and-selection-section${hasMode ? '' : ' not-enabled'}`}>
      <div className="hunter-automation-ats-logo-wrap">
        {/* `cdn-src` upstream; `/static` is proxied onto this origin. An ATS with no automation
            logo renders the wrapper and no image, which is what the partial map produces. */}
        {logo === undefined ? null : <img className="hunter-automation-ats-logo" src={asset(logo)} alt="" />}
      </div>
      <div className="hunter-automation">
        {hasMode ? (
          <WooSwitch
            className="hunter-automation-switch"
            isOn={active}
            label={active ? 'Automation active' : 'Automation paused'}
            onToggle={onToggle}
          />
        ) : (
          <button type="button" className="btn btn-primary hunter-automation-enable-btn" onClick={onActivate}>
            Activate automation
          </button>
        )}
      </div>
    </div>
  )
}
